// 🚦 Subscription Limits & Enforcement System
// Checks plan limits and enforces usage restrictions

import { db } from '../firebase.config';
import { doc, getDoc, collection, query, where, getDocs, updateDoc, addDoc, serverTimestamp } from 'firebase/firestore';
import errorNotificationService from '../services/ErrorNotificationService';

// Helper function to convert Firestore timestamp to Date
function toDate(value) {
  if (!value) return null;
  if (typeof value.toDate === 'function') return value.toDate();
  try {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  } catch {
    return null;
  }
}

// 🎯 Alert Thresholds
export const ALERT_THRESHOLDS = {
  WARNING: 0.80,     // 80% - Show warning
  CRITICAL: 0.90,    // 90% - Show critical warning
  BLOCKED: 1.0       // 100% - Block operation
};

// 🔔 Alert Types
export const ALERT_TYPES = {
  WARNING: 'warning',
  CRITICAL: 'critical',
  BLOCKED: 'blocked',
  INFO: 'info'
};

// 📊 Feature Categories Mapping
export const FEATURE_CATEGORIES = {
  // Sales features
  'invoices': { category: 'sales', field: 'invoices', displayName: 'Invoices' },
  'quotations': { category: 'sales', field: 'quotations', displayName: 'Quotations' },
  'challans': { category: 'sales', field: 'challans', displayName: 'Delivery Challans' },
  'sales_credit_notes': { category: 'sales', field: 'creditNotes', displayName: 'Sales Credit Notes' },
  'sales_debit_notes': { category: 'sales', field: 'debitNotes', displayName: 'Sales Debit Notes' },
  
  // Purchase features
  'bills': { category: 'purchase', field: 'bills', displayName: 'Purchase Bills' },
  'purchase_orders': { category: 'purchase', field: 'purchaseOrders', displayName: 'Purchase Orders' },
  'purchase_receipts': { category: 'purchase', field: 'receipts', displayName: 'Purchase Receipts' },
  'purchase_credit_notes': { category: 'purchase', field: 'creditNotes', displayName: 'Purchase Credit Notes' },
  'purchase_debit_notes': { category: 'purchase', field: 'debitNotes', displayName: 'Purchase Debit Notes' },
  
  // Payment features
  'payment_receipts': { category: 'payments', field: 'receipts', displayName: 'Payment Receipts' },
  'payments': { category: 'payments', field: 'payments', displayName: 'Payments' },
  'refunds': { category: 'payments', field: 'refunds', displayName: 'Refunds' },
  
  // Party features
  'customers': { category: 'parties', field: 'customers', displayName: 'Customers' },
  'suppliers': { category: 'parties', field: 'suppliers', displayName: 'Suppliers' },
  'employees': { category: 'parties', field: 'employees', displayName: 'Employees' },
  
  // Report features
  'gst_reports': { category: 'reports', field: 'gstReports', displayName: 'GST Reports' },
  'financial_reports': { category: 'reports', field: 'financialReports', displayName: 'Financial Reports' },
  'custom_reports': { category: 'reports', field: 'customReports', displayName: 'Custom Reports' },
  'exports': { category: 'reports', field: 'exports', displayName: 'Data Exports' },
  
  // Storage features
  'data_storage': { category: 'storage', field: 'dataStorage', displayName: 'Data Storage' },
  'file_uploads': { category: 'storage', field: 'fileUploads', displayName: 'File Uploads' },
  
  // User features
  'users': { category: 'users', field: 'userLimit', displayName: 'Users' },
  'companies': { category: 'users', field: 'companyLimit', displayName: 'Companies' }
};

/**
 * 🔍 Get company's subscription details
 * @param {string} companyId - Company ID
 * @returns {Object} Subscription data with validity info
 */
export async function getCompanySubscription(companyId) {
  try {
    // Try to get subscription from companies/{companyId}/subscription_details/subscription
    const subscriptionRef = doc(db, 'companies', companyId, 'subscription_details', 'subscription');
    const subscriptionSnap = await getDoc(subscriptionRef);
    
    if (subscriptionSnap.exists()) {
      const data = subscriptionSnap.data();
      // Try multiple possible locations for endDate
      const endDate = toDate(
        data.endDate || 
        data.basePlan?.nextBillingDate || 
        data.basePlan?.nextResetDate ||
        data.validUntil || 
        data.validityEnd
      );
      const startDate = toDate(
        data.startDate || 
        data.basePlan?.startDate ||
        data.validFrom
      );
      
      // Ensure endDate and startDate are Date objects or null
      // Also ensure basePlan dates are converted if they exist
      const basePlan = data.basePlan ? {
        ...data.basePlan,
        nextBillingDate: data.basePlan.nextBillingDate ? toDate(data.basePlan.nextBillingDate) : null,
        nextResetDate: data.basePlan.nextResetDate ? toDate(data.basePlan.nextResetDate) : null,
        startDate: data.basePlan.startDate ? toDate(data.basePlan.startDate) : null
      } : null;
      
      return {
        exists: true,
        endDate, // Ensure this is a Date object or null
        startDate, // Ensure this is a Date object or null
        status: data.status || data.basePlan?.status || 'active',
        planId: data.planId || data.basePlan?.planId || data.plan || 'free',
        planName: data.planName || data.basePlan?.planName || (data.planId || data.basePlan?.planId || 'free').toUpperCase(),
        isTrial: data.isTrial || data.basePlan?.isTrial || false,
        basePlan, // Use converted basePlan
        // Spread other data but exclude the date fields we've already converted
        ...Object.fromEntries(
          Object.entries(data).filter(([key]) => 
            !['endDate', 'startDate', 'validUntil', 'validityEnd', 'validFrom', 'basePlan'].includes(key)
          )
        )
      };
    }
    
    // Fallback: check if subscription exists in old location
    return {
      exists: false,
      endDate: null,
      startDate: null,
      status: 'inactive',
      planId: 'free',
      planName: 'FREE',
      isTrial: false
    };
  } catch (error) {
    console.error('❌ Error fetching subscription:', error);
    return {
      exists: false,
      endDate: null,
      startDate: null,
      status: 'error',
      planId: 'free',
      planName: 'FREE',
      isTrial: false
    };
  }
}

/**
 * ✅ Check if subscription is valid (not expired)
 * @param {string} companyId - Company ID
 * @returns {Object} Validity status
 */
export async function isSubscriptionValid(companyId) {
  try {
    const subscription = await getCompanySubscription(companyId);
    
    if (!subscription.exists || !subscription.endDate) {
      return {
        valid: false,
        reason: 'No active subscription found',
        daysRemaining: 0,
        subscription
      };
    }
    
    // Ensure endDate is a Date object
    let endDate = subscription.endDate;
    if (!(endDate instanceof Date)) {
      // Try to convert it if it's not already a Date
      endDate = toDate(endDate);
      if (!endDate) {
        console.error('❌ Invalid endDate format:', subscription.endDate);
        return {
          valid: false,
          reason: 'Invalid subscription end date',
          daysRemaining: 0,
          subscription
        };
      }
    }
    
    const now = Date.now();
    const endTime = endDate.getTime();
    const daysRemaining = Math.ceil((endTime - now) / (1000 * 60 * 60 * 24));
    
    // Block if expired: endTime is in the past OR daysRemaining is 0 or negative OR status is expired/cancelled
    // Also check if endTime is exactly equal to now (should be considered expired)
    const isExpiredByDate = endTime <= now;
    const isExpiredByDays = daysRemaining <= 0;
    const isExpiredByStatus = subscription.status === 'expired' || subscription.status === 'cancelled';
    const valid = !isExpiredByDate && !isExpiredByDays && !isExpiredByStatus;
    
    // Debug logging for troubleshooting
    if (!valid) {
      console.log('🚫 Subscription expired check:', {
        companyId,
        endTime: new Date(endTime).toISOString(),
        now: new Date(now).toISOString(),
        daysRemaining,
        status: subscription.status,
        isExpiredByDate,
        isExpiredByDays,
        isExpiredByStatus,
        valid
      });
    }
    
    return {
      valid,
      reason: valid ? 'Active subscription' : 'Subscription expired',
      daysRemaining: daysRemaining > 0 ? daysRemaining : 0,
      subscription
    };
  } catch (error) {
    console.error('❌ Error checking subscription validity:', error);
    return {
      valid: false,
      reason: 'Error checking subscription',
      daysRemaining: 0,
      subscription: null
    };
  }
}

/**
 * 🔍 Get company's current plan and limits
 * @param {string} companyId - Company ID
 * @returns {Object} Plan data with limits
 */
// Helper function to get feature limits based on planId
function getPlanFeatureLimits(planId) {
  const planIdLower = (planId || '').toLowerCase();
  
  // Enterprise plan - unlimited everything
  if (planIdLower === 'enterprise') {
    return {
      sales: {
        invoices: { limit: -1, unlimited: true },
        quotations: { limit: -1, unlimited: true },
        challans: { limit: -1, unlimited: true },
        creditNotes: { limit: -1, unlimited: true },
        debitNotes: { limit: -1, unlimited: true }
      },
      purchase: {
        bills: { limit: -1, unlimited: true },
        purchaseOrders: { limit: -1, unlimited: true },
        receipts: { limit: -1, unlimited: true },
        creditNotes: { limit: -1, unlimited: true },
        debitNotes: { limit: -1, unlimited: true }
      },
      payments: {
        receipts: { limit: -1, unlimited: true },
        payments: { limit: -1, unlimited: true },
        refunds: { limit: -1, unlimited: true }
      },
      parties: {
        customers: { limit: -1, unlimited: true },
        suppliers: { limit: -1, unlimited: true },
        employees: { limit: -1, unlimited: true }
      },
      reports: {
        gstReports: { limit: -1, unlimited: true },
        financialReports: { limit: -1, unlimited: true },
        customReports: { limit: -1, unlimited: true },
        exports: { limit: -1, unlimited: true }
      },
      storage: {
        dataStorage: { limit: -1, unlimited: true, unit: 'GB' },
        fileUploads: { limit: -1, unlimited: true, unit: 'MB' }
      },
      users: {
        userLimit: { limit: -1, unlimited: true },
        companyLimit: { limit: -1, unlimited: true }
      }
    };
  }
  
  // Pro plan - high limits (5-10x free)
  if (planIdLower === 'pro') {
    const freeLimits = getDefaultFreePlanLimits();
    return {
      sales: Object.fromEntries(
        Object.entries(freeLimits.sales).map(([key, value]) => [
          key,
          { limit: value.limit * 10, unlimited: false }
        ])
      ),
      purchase: Object.fromEntries(
        Object.entries(freeLimits.purchase).map(([key, value]) => [
          key,
          { limit: value.limit * 10, unlimited: false }
        ])
      ),
      payments: Object.fromEntries(
        Object.entries(freeLimits.payments).map(([key, value]) => [
          key,
          { limit: value.limit * 10, unlimited: false }
        ])
      ),
      parties: Object.fromEntries(
        Object.entries(freeLimits.parties).map(([key, value]) => [
          key,
          { limit: value.limit * 20, unlimited: false }
        ])
      ),
      reports: Object.fromEntries(
        Object.entries(freeLimits.reports).map(([key, value]) => [
          key,
          { limit: value.limit * 20, unlimited: false }
        ])
      ),
      storage: {
        dataStorage: { limit: 10, unlimited: false, unit: 'GB' },
        fileUploads: { limit: 1000, unlimited: false, unit: 'MB' }
      },
      users: {
        userLimit: { limit: 5, unlimited: false },
        companyLimit: { limit: 3, unlimited: false }
      }
    };
  }
  
  // Basic/Starter plans - moderate limits (2-3x free)
  if (planIdLower === 'basic' || planIdLower === 'starter') {
    const freeLimits = getDefaultFreePlanLimits();
    return {
      sales: Object.fromEntries(
        Object.entries(freeLimits.sales).map(([key, value]) => [
          key,
          { limit: value.limit * 3, unlimited: false }
        ])
      ),
      purchase: Object.fromEntries(
        Object.entries(freeLimits.purchase).map(([key, value]) => [
          key,
          { limit: value.limit * 3, unlimited: false }
        ])
      ),
      payments: Object.fromEntries(
        Object.entries(freeLimits.payments).map(([key, value]) => [
          key,
          { limit: value.limit * 3, unlimited: false }
        ])
      ),
      parties: Object.fromEntries(
        Object.entries(freeLimits.parties).map(([key, value]) => [
          key,
          { limit: value.limit * 5, unlimited: false }
        ])
      ),
      reports: Object.fromEntries(
        Object.entries(freeLimits.reports).map(([key, value]) => [
          key,
          { limit: value.limit * 5, unlimited: false }
        ])
      ),
      storage: {
        dataStorage: { limit: 3, unlimited: false, unit: 'GB' },
        fileUploads: { limit: 300, unlimited: false, unit: 'MB' }
      },
      users: {
        userLimit: { limit: 2, unlimited: false },
        companyLimit: { limit: 1, unlimited: false }
      }
    };
  }
  
  // Default to free plan limits
  return getDefaultFreePlanLimits();
}

// Helper function to convert subscription limits to features format
function convertLimitsToFeatures(limits, planId) {
  // If limits already has features structure, return it
  if (limits && limits.features && typeof limits.features === 'object') {
    return limits.features;
  }
  
  // If limits has nested structure (sales, purchase, etc.), return as is
  if (limits && (limits.sales || limits.purchase || limits.parties)) {
    return limits;
  }
  
  // Otherwise, use planId to determine feature limits
  return getPlanFeatureLimits(planId);
}

export async function getCompanyPlanLimits(companyId) {
  try {
    console.log('📋 Fetching plan limits for company:', companyId);
    
    // Get subscription first to check validity
    const subscription = await getCompanySubscription(companyId);
    
    // Get company data
    const companyRef = doc(db, 'companies', companyId);
    const companySnap = await getDoc(companyRef);
    
    if (!companySnap.exists()) {
      throw new Error('Company not found');
    }
    
    const companyData = companySnap.data();
    const planId = subscription.planId || subscription.basePlan?.planId || companyData.currentPlanId || 'free';
    
    console.log('📋 Company plan ID:', planId);
    
    // Try to get plan data from plans collection
    const planRef = doc(db, 'plans', planId);
    const planSnap = await getDoc(planRef);
    
    let features = {};
    let planName = planId.toUpperCase();
    
    if (planSnap.exists()) {
      // Plan document exists - use it
      const planData = planSnap.data();
      features = planData.features || {};
      planName = planData.name || planName;
      console.log('✅ Using plan data from plans collection');
    } else {
      // Plan document not found - use subscription limits
      console.warn('⚠️ Plan document not found in plans collection, using subscription limits');
      
      // Get limits from subscription
      const subscriptionLimits = subscription.basePlan?.limits || subscription.limits || {};
      
      // Convert limits to features format
      features = convertLimitsToFeatures(subscriptionLimits, planId);
      planName = subscription.planName || subscription.basePlan?.planName || planName;
      
      console.log('✅ Using subscription limits converted to features format');
    }
    
    return {
      planId,
      planName,
      features,
      usageCounters: companyData.usageCounters || {},
      subscription
    };
  } catch (error) {
    console.error('❌ Error fetching plan limits:', error);
    throw error;
  }
}

/**
 * 📊 Check if feature usage is within limits
 * @param {string} companyId - Company ID
 * @param {string} featureKey - Feature key (e.g., 'invoices', 'customers')
 * @returns {Object} Usage status and limits
 */
export async function checkFeatureLimit(companyId, featureKey) {
  try {
    const featureConfig = FEATURE_CATEGORIES[featureKey];
    
    if (!featureConfig) {
      console.warn('⚠️ Unknown feature:', featureKey);
      return {
        allowed: true,
        unlimited: true,
        reason: 'Unknown feature - allowing by default'
      };
    }
    
    const { planId, planName, features, usageCounters } = await getCompanyPlanLimits(companyId);
    
    const { category, field, displayName } = featureConfig;
    
    // Get feature limit from plan
    const featureLimit = features[category]?.[field];
    
    if (!featureLimit) {
      console.warn('⚠️ No limit defined for:', displayName);
      return {
        allowed: true,
        unlimited: true,
        reason: 'No limit defined - allowing by default'
      };
    }
    
    // Check if unlimited
    if (featureLimit.unlimited === true) {
      return {
        allowed: true,
        unlimited: true,
        limit: 0,
        current: 0,
        remaining: 'Unlimited',
        percentage: 0,
        planName,
        featureName: displayName
      };
    }
    
    // Get current usage
    const currentMonth = new Date().toISOString().slice(0, 7); // YYYY-MM
    const monthlyUsage = usageCounters.currentMonth === currentMonth ? usageCounters : {};
    const currentUsage = monthlyUsage[category]?.[field] || 0;
    const limit = featureLimit.limit || 0;
    
    // Calculate usage percentage
    const percentage = limit > 0 ? (currentUsage / limit) : 0;
    const remaining = Math.max(0, limit - currentUsage);
    const allowed = currentUsage < limit;
    
    // Determine alert level
    let alertLevel = null;
    if (percentage >= ALERT_THRESHOLDS.BLOCKED) {
      alertLevel = ALERT_TYPES.BLOCKED;
    } else if (percentage >= ALERT_THRESHOLDS.CRITICAL) {
      alertLevel = ALERT_TYPES.CRITICAL;
    } else if (percentage >= ALERT_THRESHOLDS.WARNING) {
      alertLevel = ALERT_TYPES.WARNING;
    }
    
    return {
      allowed,
      unlimited: false,
      limit,
      current: currentUsage,
      remaining,
      percentage: Math.round(percentage * 100),
      alertLevel,
      planId,
      planName,
      featureName: displayName,
      category,
      field
    };
  } catch (error) {
    console.error('❌ Error checking feature limit:', error);
    // On error, allow operation but log warning
    return {
      allowed: true,
      unlimited: false,
      error: error.message,
      reason: 'Error checking limits - allowing by default'
    };
  }
}

/**
 * 🚫 Check and enforce feature limit before operation
 * @param {string} companyId - Company ID
 * @param {string} featureKey - Feature key
 * @param {boolean} throwOnBlock - Throw error if blocked
 * @param {string} operationType - 'read' or 'write' (default: 'write')
 * @returns {Object} Validation result
 */
export async function enforceFeatureLimit(companyId, featureKey, throwOnBlock = false, operationType = 'write') {
  try {
    // First, check if subscription is valid
    const validityCheck = await isSubscriptionValid(companyId);
    
    if (!validityCheck.valid) {
      // Subscription expired - block WRITE operations, allow READ operations
      if (operationType === 'write') {
        await logUsageAlert(companyId, {
          type: ALERT_TYPES.BLOCKED,
          featureKey: 'subscription_expired',
          featureName: 'Subscription',
          current: 0,
          limit: 0,
          planName: validityCheck.subscription?.planName || 'Unknown',
          message: `Your subscription has expired. Please renew your plan to continue using the service.`,
          action: 'subscription_expired',
          daysRemaining: validityCheck.daysRemaining
        });
        
        if (throwOnBlock) {
          const error = new Error(`Your subscription has expired. Please renew your plan to continue.`);
          error.code = 'SUBSCRIPTION_EXPIRED';
          error.validityCheck = validityCheck;
          error.daysRemaining = validityCheck.daysRemaining;
          
          // Use ErrorNotificationService to handle and display error
          errorNotificationService.handleSubscriptionError(error, {
            context: 'FeatureLimitEnforcement',
            showNotification: true
          });
          
          throw error;
        }
        
        return {
          success: false,
          blocked: true,
          allowed: false, // Explicitly set to false to block operation
          subscriptionExpired: true,
          reason: validityCheck.reason,
          daysRemaining: validityCheck.daysRemaining,
          validityCheck
        };
      } else {
        // For read operations, allow without any checks when subscription expired
        // This provides better UX - users can still view their data
        console.log('📖 Subscription expired, but allowing read operation for better UX');
        // Return success to allow the read operation
        return {
          success: true,
          blocked: false,
          subscriptionExpired: true,
          allowed: true, // Explicitly allow read operations
          reason: 'Read operations allowed even when subscription expired',
          validityCheck
        };
      }
    }
    
    // Check feature limits
    const limitCheck = await checkFeatureLimit(companyId, featureKey);
    
    if (!limitCheck.allowed) {
      // Log blocked operation
      await logUsageAlert(companyId, {
        type: ALERT_TYPES.BLOCKED,
        featureKey,
        featureName: limitCheck.featureName,
        current: limitCheck.current,
        limit: limitCheck.limit,
        planName: limitCheck.planName,
        message: `Operation blocked: ${limitCheck.featureName} limit reached (${limitCheck.current}/${limitCheck.limit})`,
        action: 'blocked_operation'
      });
      
      if (throwOnBlock) {
        const error = new Error(`Plan limit reached for ${limitCheck.featureName}. Please upgrade your plan.`);
        error.code = 'FEATURE_LIMIT_EXCEEDED';
        error.limitCheck = limitCheck;
        
        // Use ErrorNotificationService to handle and display error
        errorNotificationService.handleSubscriptionError(error, {
          context: 'FeatureLimitEnforcement',
          showNotification: true
        });
        
        throw error;
      }
      
      return {
        success: false,
        blocked: true,
        subscriptionExpired: false,
        ...limitCheck
      };
    }
    
    // Check if warning threshold reached
    if (limitCheck.alertLevel === ALERT_TYPES.CRITICAL || limitCheck.alertLevel === ALERT_TYPES.WARNING) {
      await logUsageAlert(companyId, {
        type: limitCheck.alertLevel,
        featureKey,
        featureName: limitCheck.featureName,
        current: limitCheck.current,
        limit: limitCheck.limit,
        planName: limitCheck.planName,
        percentage: limitCheck.percentage,
        message: `${limitCheck.alertLevel === ALERT_TYPES.CRITICAL ? 'Critical' : 'Warning'}: ${limitCheck.featureName} usage at ${limitCheck.percentage}%`,
        action: 'threshold_alert'
      });
    }
    
    return {
      success: true,
      blocked: false,
      subscriptionExpired: false,
      ...limitCheck
    };
  } catch (error) {
    console.error('❌ Error enforcing feature limit:', error);
    throw error;
  }
}

/**
 * 📝 Log usage alert to Firestore
 * @param {string} companyId - Company ID
 * @param {Object} alertData - Alert data
 */
export async function logUsageAlert(companyId, alertData) {
  try {
    const alertsRef = collection(db, 'companies', companyId, 'usageAlerts');
    
    await addDoc(alertsRef, {
      ...alertData,
      timestamp: serverTimestamp(),
      read: false,
      dismissed: false
    });
    
    console.log('📝 Usage alert logged:', alertData.type, alertData.message);
  } catch (error) {
    console.error('❌ Error logging usage alert:', error);
    // Don't throw - alerting is not critical
  }
}

/**
 * 🔔 Get unread usage alerts for company
 * @param {string} companyId - Company ID
 * @param {number} limit - Max alerts to return
 * @returns {Array} Unread alerts
 */
export async function getUnreadAlerts(companyId, limit = 10) {
  try {
    const alertsRef = collection(db, 'companies', companyId, 'usageAlerts');
    const q = query(
      alertsRef,
      where('read', '==', false),
      where('dismissed', '==', false)
    );
    
    const querySnapshot = await getDocs(q);
    const alerts = [];
    
    querySnapshot.forEach((doc) => {
      alerts.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    // Sort by timestamp (newest first)
    alerts.sort((a, b) => {
      const timeA = a.timestamp?.toMillis() || 0;
      const timeB = b.timestamp?.toMillis() || 0;
      return timeB - timeA;
    });
    
    return alerts.slice(0, limit);
  } catch (error) {
    console.error('❌ Error fetching unread alerts:', error);
    return [];
  }
}

/**
 * ✅ Mark alert as read
 * @param {string} companyId - Company ID
 * @param {string} alertId - Alert ID
 */
export async function markAlertAsRead(companyId, alertId) {
  try {
    const alertRef = doc(db, 'companies', companyId, 'usageAlerts', alertId);
    await updateDoc(alertRef, {
      read: true,
      readAt: serverTimestamp()
    });
    
    console.log('✅ Alert marked as read:', alertId);
  } catch (error) {
    console.error('❌ Error marking alert as read:', error);
  }
}

/**
 * 🗑️ Dismiss alert
 * @param {string} companyId - Company ID
 * @param {string} alertId - Alert ID
 */
export async function dismissAlert(companyId, alertId) {
  try {
    const alertRef = doc(db, 'companies', companyId, 'usageAlerts', alertId);
    await updateDoc(alertRef, {
      dismissed: true,
      dismissedAt: serverTimestamp()
    });
    
    console.log('🗑️ Alert dismissed:', alertId);
  } catch (error) {
    console.error('❌ Error dismissing alert:', error);
  }
}

/**
 * 📊 Get usage summary for all features
 * @param {string} companyId - Company ID
 * @returns {Object} Usage summary
 */
export async function getUsageSummary(companyId) {
  try {
    const { planId, planName, features, usageCounters } = await getCompanyPlanLimits(companyId);
    
    const currentMonth = new Date().toISOString().slice(0, 7);
    const monthlyUsage = usageCounters.currentMonth === currentMonth ? usageCounters : {};
    
    const summary = {
      planId,
      planName,
      currentMonth,
      categories: {}
    };
    
    // Iterate through all feature categories
    for (const [featureKey, featureConfig] of Object.entries(FEATURE_CATEGORIES)) {
      const { category, field, displayName } = featureConfig;
      
      if (!summary.categories[category]) {
        summary.categories[category] = {
          categoryName: category,
          features: []
        };
      }
      
      const featureLimit = features[category]?.[field];
      
      if (featureLimit) {
        const currentUsage = monthlyUsage[category]?.[field] || 0;
        const limit = featureLimit.limit || 0;
        const unlimited = featureLimit.unlimited === true;
        const percentage = unlimited ? 0 : (limit > 0 ? (currentUsage / limit) : 0);
        
        let alertLevel = null;
        if (!unlimited && percentage >= ALERT_THRESHOLDS.BLOCKED) {
          alertLevel = ALERT_TYPES.BLOCKED;
        } else if (!unlimited && percentage >= ALERT_THRESHOLDS.CRITICAL) {
          alertLevel = ALERT_TYPES.CRITICAL;
        } else if (!unlimited && percentage >= ALERT_THRESHOLDS.WARNING) {
          alertLevel = ALERT_TYPES.WARNING;
        }
        
        summary.categories[category].features.push({
          featureKey,
          displayName,
          current: currentUsage,
          limit: unlimited ? 'Unlimited' : limit,
          unlimited,
          percentage: Math.round(percentage * 100),
          remaining: unlimited ? 'Unlimited' : Math.max(0, limit - currentUsage),
          alertLevel
        });
      }
    }
    
    return summary;
  } catch (error) {
    console.error('❌ Error getting usage summary:', error);
    throw error;
  }
}

/**
 * 🔄 Get default free plan limits
 * @returns {Object} Default limits
 */
function getDefaultFreePlanLimits() {
  return {
    sales: {
      invoices: { limit: 50, unlimited: false },
      quotations: { limit: 25, unlimited: false },
      challans: { limit: 25, unlimited: false },
      creditNotes: { limit: 10, unlimited: false },
      debitNotes: { limit: 10, unlimited: false }
    },
    purchase: {
      bills: { limit: 50, unlimited: false },
      purchaseOrders: { limit: 25, unlimited: false },
      receipts: { limit: 25, unlimited: false },
      creditNotes: { limit: 10, unlimited: false },
      debitNotes: { limit: 10, unlimited: false }
    },
    payments: {
      receipts: { limit: 100, unlimited: false },
      payments: { limit: 100, unlimited: false },
      refunds: { limit: 10, unlimited: false }
    },
    parties: {
      customers: { limit: 100, unlimited: false },
      suppliers: { limit: 50, unlimited: false },
      employees: { limit: 5, unlimited: false }
    },
    reports: {
      gstReports: { limit: 12, unlimited: false },
      financialReports: { limit: 12, unlimited: false },
      customReports: { limit: 5, unlimited: false },
      exports: { limit: 5, unlimited: false }
    },
    storage: {
      dataStorage: { limit: 1, unlimited: false, unit: 'GB' },
      fileUploads: { limit: 100, unlimited: false, unit: 'MB' }
    },
    users: {
      userLimit: { limit: 1, unlimited: false },
      companyLimit: { limit: 1, unlimited: false }
    }
  };
}

/**
 * 🎯 Simplified check for common operations
 * Use this before creating invoices, customers, etc.
 */
export async function canPerformOperation(companyId, operationType) {
  // First check subscription validity
  const validityCheck = await isSubscriptionValid(companyId);
  if (!validityCheck.valid) {
    return false;
  }
  
  const limitCheck = await checkFeatureLimit(companyId, operationType);
  return limitCheck.allowed;
}

/**
 * 🚀 Enforce operation with user-friendly error
 * Throws error with upgrade suggestion if blocked
 */
export async function enforceOperation(companyId, operationType) {
  return await enforceFeatureLimit(companyId, operationType, true);
}

/**
 * 📅 Check and create plan expiry reminders
 * Should be called periodically to check for upcoming expiries
 * @param {string} companyId - Company ID
 */
export async function checkAndCreateExpiryReminders(companyId) {
  try {
    const validityCheck = await isSubscriptionValid(companyId);
    const subscription = validityCheck.subscription;
    
    if (!subscription || !subscription.endDate) {
      return; // No subscription to check
    }
    
    const daysRemaining = validityCheck.daysRemaining;
    
    // Check if we should create reminders
    const reminderDays = [7, 3, 1]; // Days before expiry to remind
    
    for (const reminderDay of reminderDays) {
      if (daysRemaining === reminderDay || (daysRemaining < reminderDay && daysRemaining > reminderDay - 1)) {
        // Check if we already created this reminder
        const alertsRef = collection(db, 'companies', companyId, 'usageAlerts');
        const q = query(
          alertsRef,
          where('type', '==', daysRemaining <= 1 ? ALERT_TYPES.CRITICAL : ALERT_TYPES.WARNING),
          where('action', '==', 'plan_expiry_reminder'),
          where('dismissed', '==', false)
        );
        
        const existingAlerts = await getDocs(q);
        
        // Only create if we don't have an active reminder for this day
        if (existingAlerts.empty) {
          const alertType = daysRemaining <= 1 ? ALERT_TYPES.CRITICAL : 
                           daysRemaining <= 3 ? ALERT_TYPES.CRITICAL : 
                           ALERT_TYPES.WARNING;
          
          const message = daysRemaining <= 1 
            ? `⚠️ Your subscription expires today! Renew now to avoid service interruption.`
            : daysRemaining <= 3
            ? `⚠️ Your subscription expires in ${daysRemaining} day${daysRemaining > 1 ? 's' : ''}. Renew now to continue using all features.`
            : `Your subscription expires in ${daysRemaining} days. Consider renewing your plan soon.`;
          
          await logUsageAlert(companyId, {
            type: alertType,
            featureKey: 'plan_expiry',
            featureName: 'Subscription',
            current: daysRemaining,
            limit: 0,
            planName: subscription.planName || 'Unknown',
            message,
            action: 'plan_expiry_reminder',
            daysRemaining,
            expiryDate: subscription.endDate
          });
          
          console.log(`📅 Created expiry reminder for company ${companyId}: ${daysRemaining} days remaining`);
        }
      }
    }
    
    // Create expired alert if subscription has expired
    if (!validityCheck.valid && daysRemaining <= 0) {
      const alertsRef = collection(db, 'companies', companyId, 'usageAlerts');
      const q = query(
        alertsRef,
        where('type', '==', ALERT_TYPES.BLOCKED),
        where('action', '==', 'subscription_expired'),
        where('dismissed', '==', false)
      );
      
      const existingAlerts = await getDocs(q);
      
      if (existingAlerts.empty) {
        await logUsageAlert(companyId, {
          type: ALERT_TYPES.BLOCKED,
          featureKey: 'subscription_expired',
          featureName: 'Subscription',
          current: 0,
          limit: 0,
          planName: subscription.planName || 'Unknown',
          message: `🚫 Your subscription has expired. All operations are blocked. Please renew your plan to continue.`,
          action: 'subscription_expired',
          daysRemaining: 0,
          expiryDate: subscription.endDate
        });
        
        console.log(`🚫 Created expired subscription alert for company ${companyId}`);
      }
    }
  } catch (error) {
    console.error('❌ Error checking expiry reminders:', error);
  }
}

export default {
  checkFeatureLimit,
  enforceFeatureLimit,
  getCompanyPlanLimits,
  getCompanySubscription,
  isSubscriptionValid,
  checkAndCreateExpiryReminders,
  logUsageAlert,
  getUnreadAlerts,
  markAlertAsRead,
  dismissAlert,
  getUsageSummary,
  canPerformOperation,
  enforceOperation,
  ALERT_THRESHOLDS,
  ALERT_TYPES,
  FEATURE_CATEGORIES
};

